import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { dashboardActions } from 'features/Dashboard/dashboardActions'
import { Locations } from 'shared/Router/Locations'
import WelcomeMessage from './components/WelcomeMessage'
import ServicesGeographicGrid from './components/ServicesGeographicGrid'
import CommutersTopGrid from './components/CommutersTopGrid'
import OverviewSection from './components/OverviewSection'
import ExceptionCard from './components/ExceptionCard'
// import BottomStatsGrid from './components/BottomStatsGrid'
import { useNavigate } from 'react-router-dom'
import { isOnboarding } from 'features/Onboarding/helperFunctions'
import AsyncItemsView from 'features/Onboarding/dashboard/AsyncItemsView'
import styled from 'styled-components'
import {
  DashboardLocationCard,
  DashboardPeriod,
  DashboardProgramCard,
} from './dashboardTypes'
import PeriodSelector from './components/PeriodSelector'

const Container = styled.div`
  padding: 2rem;
`

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2rem;
`

const PeriodSlot = styled.div`
  margin-left: auto;
`

const DashboardView = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector((state: AppState) => state.user.profile)
  const onboarding = useSelector((state: AppState) => state.onboarding)
  const [programPeriod, setProgramPeriod] = useState<DashboardPeriod>('all')

  const {
    companyTaxSavings,
    trackers,
    programCards,
    locationCards,
    topServices,
    worksites,
    transitExceptions,
  } = useSelector((state: AppState) => state.dashboard)

  useEffect(() => {
    dispatch(dashboardActions.getDashboardTrackers())
    dispatch(dashboardActions.getDashboardBulkData())
    dispatch(dashboardActions.getDashboardProgramCards())
    dispatch(dashboardActions.getDashboardLocationCards())
    dispatch(dashboardActions.getDashboardWorksites())
    dispatch(dashboardActions.getDashboardTransitExceptions())
  }, [])

  const handleLocationCardClick = (card: DashboardLocationCard) => {
    navigate(
      Locations.DashboardLocation.replace(
        ':locationKey',
        encodeURIComponent(card.locationKey)
      )
    )
  }

  const handleProgramCardClick = (card: DashboardProgramCard) => {
    navigate(
      Locations.DashboardProgram.replace(
        ':launchId',
        encodeURIComponent(card.launchId)
      )
    )
  }

  return (
    <>
      {isOnboarding(onboarding) ? (
        <AsyncItemsView />
      ) : (
        <Container>
          <PageHeader>
            <WelcomeMessage user={user} />
            <PeriodSlot>
              <PeriodSelector
                value={programPeriod}
                onChange={setProgramPeriod}
              />
            </PeriodSlot>
          </PageHeader>
          <CommutersTopGrid
            trackers={trackers}
            period={programPeriod}
            taxSavings={companyTaxSavings}
          />
          <ServicesGeographicGrid
            topServices={topServices}
            period={programPeriod}
            onViewFullReport={() => navigate(Locations.Reporting)}
            worksites={worksites}
          />
          <OverviewSection
            locationCards={locationCards}
            programCards={programCards}
            locationsLoading={locationCards === null}
            programsLoading={programCards === null}
            period={programPeriod}
            onLocationClick={handleLocationCardClick}
            onProgramClick={handleProgramCardClick}
            onAddSite={() =>
              navigate(`${Locations.Segments.Overview}?tab=worksites`)
            }
            onAddProgram={() => navigate(Locations.Programs.Launch)}
          />
          <ExceptionCard
            exceptions={transitExceptions}
            onViewAllClick={() => navigate(Locations.Segments.Overview)}
          />
          {/* Temporarily hidden: Tax Savings, Carbon Reduction, Time Saved, Wellness Score
          <BottomStatsGrid
            companyTaxSavings={metrics?.companyTaxSavings}
            carbonMetric={metrics?.carbonFootprint}
            timeMetric={metrics?.timeSaved}
            wellnessMetric={metrics?.wellness}
          />
          */}
        </Container>
      )}
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  DashboardView.displayName = 'DashboardView'
}
export default DashboardView
